import { useState, useEffect } from 'react';
import RichTextEditor from '../../components/common/RichTextEditor';
import './QuestionModal.css';

function QuestionModal({ question, onSave, onClose, isSaving }) {
  const isEditing = Boolean(question?.id);

  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    displayOrder: 0,
    isPublished: true,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (question) {
      setFormData({
        id: question.id,
        question: question.question || '',
        answer: question.answer || '',
        displayOrder: question.displayOrder ?? 0,
        isPublished: question.isPublished ?? true,
      });
    }
  }, [question]);

  const validate = () => {
    const newErrors = {};

    if (!formData.question.trim()) {
      newErrors.question = 'Question is required';
    } else if (formData.question.length > 500) {
      newErrors.question = 'Question must be 500 characters or less';
    }

    if (!formData.answer.trim() || formData.answer === '<p><br></p>') {
      newErrors.answer = 'Answer is required';
    }

    if (formData.displayOrder < 0) {
      newErrors.displayOrder = 'Display order must be 0 or greater';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Convert Quill alignment classes to inline styles for cross-site compatibility
  const convertAlignmentToInlineStyles = (html) => {
    if (!html) return html;

    return html
      // Convert Quill alignment classes to inline styles
      .replace(/class="ql-align-center"/g, 'style="text-align: center;"')
      .replace(/class="ql-align-right"/g, 'style="text-align: right;"')
      .replace(/class="ql-align-justify"/g, 'style="text-align: justify;"')
      // Add explicit left-alignment to paragraphs without alignment
      // This ensures left-aligned text stays left even if ecommerce CSS centers by default
      .replace(/<p>/g, '<p style="text-align: left;">');
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Transform the answer HTML before saving
    const dataToSave = {
      ...formData,
      answer: convertAlignmentToInlineStyles(formData.answer),
    };

    onSave(dataToSave);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !isSaving) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose, isSaving]);

  return (
    <div className="modal-overlay" onClick={!isSaving ? onClose : undefined}>
      <div className="question-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditing ? 'Edit Question' : 'Add Question'}</h2>
          <button
            className="close-button"
            onClick={onClose}
            disabled={isSaving}
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="q-question">
                Question <span className="required">*</span>
              </label>
              <textarea
                id="q-question"
                value={formData.question}
                onChange={(e) => handleChange('question', e.target.value)}
                placeholder="Enter the question..."
                className={errors.question ? 'error' : ''}
                rows={3}
                maxLength={500}
              />
              {errors.question && <span className="error-message">{errors.question}</span>}
              <span className="char-count">{formData.question.length}/500</span>
            </div>

            <div className="form-group">
              <label>
                Answer <span className="required">*</span>
              </label>
              <RichTextEditor
                value={formData.answer}
                onChange={(value) => handleChange('answer', value)}
                placeholder="Enter the answer (supports formatting)..."
                minHeight={200}
                className={errors.answer ? 'error' : ''}
              />
              {errors.answer && <span className="error-message">{errors.answer}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="q-displayOrder">Display Order</label>
                <input
                  id="q-displayOrder"
                  type="number"
                  value={formData.displayOrder}
                  onChange={(e) => handleChange('displayOrder', parseInt(e.target.value) || 0)}
                  min="0"
                  className={errors.displayOrder ? 'error' : ''}
                />
                {errors.displayOrder && <span className="error-message">{errors.displayOrder}</span>}
                <span className="help-text">Lower numbers appear first</span>
              </div>

              <div className="form-group">
                <label>Status</label>
                <div className="status-toggle">
                  <button
                    type="button"
                    className={`status-option ${formData.isPublished ? 'active published' : ''}`}
                    onClick={() => handleChange('isPublished', true)}
                  >
                    Published
                  </button>
                  <button
                    type="button"
                    className={`status-option ${!formData.isPublished ? 'active draft' : ''}`}
                    onClick={() => handleChange('isPublished', false)}
                  >
                    Draft
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <span className="btn-spinner"></span>
                  Saving...
                </>
              ) : (
                isEditing ? 'Update Question' : 'Add Question'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default QuestionModal;
